import React from 'react';
import { motion } from 'framer-motion';
import { 
  DocumentTextIcon,
  GlobeAltIcon,
  UserIcon,
  CreditCardIcon,
  ShieldExclamationIcon,
  ScaleIcon,
  LockClosedIcon,
  ExclamationTriangleIcon,
  EnvelopeIcon,
  MapPinIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const TermsOfService: React.FC = () => {
  const effectiveDate = 'May 15, 2026';

  return (
    <div className="min-h-screen py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold text-primary bg-primary/10 border border-primary/20 rounded-full mb-4">
            <DocumentTextIcon className="w-4 h-4" />
            Terms of Service
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-text-main mb-4 tracking-tight leading-tight">
            Terms of Service
          </h1>
          <p className="text-lg text-text-muted max-w-2xl mx-auto">
            Effective Date: <span className="text-text-main font-medium">{effectiveDate}</span>  ·  Applies to all NotesCrafter platforms
          </p>
        </motion.div>

        {/* Section 1: Acceptance of Terms */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-glass/30 backdrop-blur-md border border-border-glow rounded-2xl p-8 mb-8 hover:border-primary/50 transition-colors shadow-xl"
        >
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="bg-gradient-to-br from-primary/20 to-secondary/20 border border-primary/30 p-4 rounded-xl shadow-inner">
              <ShieldExclamationIcon className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-text-main mb-3">1. Acceptance of Terms</h2>
              <p className="text-text-muted leading-relaxed">
                By accessing or using any NotesCrafter service — including <code className="px-1.5 py-0.5 text-xs text-primary bg-primary/10 border border-primary/20 rounded">flow.notescrafter.com</code>, <code className="px-1.5 py-0.5 text-xs text-primary bg-primary/10 border border-primary/20 rounded">tools.notescrafter.com</code>, <code className="px-1.5 py-0.5 text-xs text-primary bg-primary/10 border border-primary/20 rounded">print.notescrafter.com</code>, or the NotesCrafter Android application — you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree, please discontinue use immediately.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Detailed Sections */}
        <div className="space-y-8">
          
          {/* Section 2: About NotesCrafter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <GlobeAltIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">2. About NotesCrafter</h2>
            </div>
            <div className="pl-0 sm:pl-12">
              <p className="text-text-muted leading-relaxed">
                NotesCrafter is a sole proprietorship based in Aurangabad, Bihar, India. We provide browser-based and app-based document tools to help students convert online lectures, slides, and study material into clean, printable notes — along with a physical print-and-deliver service.
              </p>
            </div>
          </motion.div>

          {/* Section 3: Eligibility */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <UserIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">3. Eligibility</h2>
            </div>
            <div className="pl-0 sm:pl-12 text-text-muted leading-relaxed space-y-3">
              <p>
                You must be at least 13 years old to use NotesCrafter. By using our services, you represent that you meet this age requirement. Users under 18 must have the consent of a parent or legal guardian.
              </p>
            </div>
          </motion.div>

          {/* Section 4: Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <DocumentTextIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">4. Services</h2>
            </div>
            
            <div className="pl-0 sm:pl-12 space-y-6">
              {/* 4.1 */}
              <div className="border-l-2 border-primary/30 pl-4 py-1">
                <h3 className="text-lg font-bold text-text-main mb-2">4.1 Flow — flow.notescrafter.com & Android App</h3>
                <p className="text-text-muted leading-relaxed">
                  Flow is a multi-step browser-based workflow that converts online lectures, PDFs, and slides into well-formatted, print-ready notes. All processing happens locally in your browser or on your device — no files are ever uploaded to our servers.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                  <div className="bg-primary/5 p-4 rounded-lg border border-primary/10 text-sm">
                    <p className="font-semibold text-text-main mb-1.5">Web Platform:</p>
                    <ul className="list-disc list-inside space-y-1 text-text-muted">
                      <li>Free users: 5MB upload limit / session</li>
                      <li>Community Pass: Unlimited file size</li>
                    </ul>
                  </div>
                  <div className="bg-primary/5 p-4 rounded-lg border border-primary/10 text-sm">
                    <p className="font-semibold text-text-main mb-1.5">Android App:</p>
                    <ul className="list-disc list-inside space-y-1 text-text-muted">
                      <li>Full Flow access with no limits (Free)</li>
                      <li>Ads served to free users</li>
                      <li>Community Pass is fully ad-free</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 4.2 */}
              <div className="border-l-2 border-primary/30 pl-4 py-1">
                <h3 className="text-lg font-bold text-text-main mb-2">4.2 Tools — tools.notescrafter.com & Android App</h3>
                <p className="text-text-muted leading-relaxed">
                  Tools is a collection of browser-based document utilities freely available to all users. All processing occurs locally in your browser or on your device with zero server interaction. No account is required to use Tools on the web. The Android app provides the same Tools suite with the same zero server-touch processing.
                </p>
              </div>

              {/* 4.3 */}
              <div className="border-l-2 border-primary/30 pl-4 py-1">
                <h3 className="text-lg font-bold text-text-main mb-2">4.3 Print — print.notescrafter.com & Android App</h3>
                <p className="text-text-muted leading-relaxed">
                  Print is a physical print-and-deliver service. You upload a PDF, we print and wiro-bind it as an A4 notebook, and deliver it to your address anywhere in India via Shiprocket.
                </p>
                <ul className="list-disc list-inside text-text-muted mt-2 space-y-1 text-sm bg-primary/5 p-4 rounded-lg border border-primary/10">
                  <li>Available via both the website and the Android app</li>
                  <li>Payments are processed by <span className="text-text-main font-semibold">Razorpay</span></li>
                  <li>Delivery is handled by <span className="text-text-main font-semibold">Shiprocket</span></li>
                  <li>All print orders are custom-produced and non-refundable</li>
                  <li>Delivery is currently available across India only</li>
                </ul>
                <p className="text-xs text-text-muted/80 mt-2">
                  For complete delivery timelines and terms, please refer to our Shipping Policy.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Section 5: Community Pass */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <CreditCardIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">5. Community Pass</h2>
            </div>
            <div className="pl-0 sm:pl-12 space-y-4">
              <p className="text-text-muted leading-relaxed">
                Community Pass grants premium access on the web platform and a fully ad-free experience across all features in the Android app. Four membership tiers are available:
              </p>
              <ul className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                {[
                  { name: 'Silver Pass', time: '1-month access' },
                  { name: 'Gold Pass', time: '6-month access' },
                  { name: 'Platinum Pass', time: '1-year access' },
                  { name: 'Diamond Pass', time: 'Lifetime access' }
                ].map((tier, i) => (
                  <li key={i} className="bg-primary/5 border border-primary/10 p-3 rounded-lg flex flex-col justify-center">
                    <strong className="text-text-main block font-semibold text-sm sm:text-base">{tier.name}</strong>
                    <span className="text-xs text-text-muted/80 mt-1">{tier.time}</span>
                  </li>
                ))}
              </ul>
              <p className="text-text-muted text-sm leading-relaxed mt-4">
                All Community Pass purchases are made on the NotesCrafter website via Razorpay. Passes are personal and non-transferable. We reserve the right to revoke a pass if the account is found to be in violation of these Terms.
              </p>
            </div>
          </motion.div>

          {/* Section 6: Community Pass Upgrades */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <ArrowPathIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">6. Community Pass Upgrades</h2>
            </div>
            <div className="pl-0 sm:pl-12 space-y-4 text-text-muted leading-relaxed">
              <p>
                You may upgrade your active Community Pass to a higher tier at any time. When you initiate an upgrade, we calculate the remaining monetary value of your current pass based on unused days and apply it as a discount towards the price of the higher-tier pass.
              </p>
              
              <div className="bg-primary/5 border border-primary/15 rounded-xl p-5 my-4">
                <h4 className="font-semibold text-text-main mb-2">Upgrade Calculation Formula:</h4>
                <code className="block bg-glass border border-border-glow rounded-lg p-3 text-xs sm:text-sm text-primary mb-2 overflow-x-auto whitespace-nowrap">
                  Remaining Value = (Original Pass Price ÷ Total Pass Days) × Days Remaining
                </code>
                <code className="block bg-glass border border-border-glow rounded-lg p-3 text-xs sm:text-sm text-primary overflow-x-auto whitespace-nowrap">
                  Amount Due = Higher Tier Price − Remaining Value
                </code>
              </div>

              <p className="text-sm bg-primary/5 p-4 rounded-lg border border-primary/10">
                <span className="font-semibold text-text-main">For example:</span> If you paid ₹199 for a Silver Pass (30 days) and have 15 days remaining, your remaining value is ₹99.50, which is deducted from the Gold Pass price at the time of upgrade.
              </p>

              <div className="mt-4">
                <h4 className="font-bold text-text-main mb-2">Conditions:</h4>
                <ul className="space-y-2">
                  {[
                    'Upgrades are available only from a lower tier to a higher tier',
                    'Downgrades between tiers are not permitted',
                    'The Diamond (Lifetime) Pass cannot be upgraded further',
                    'Upgrade pricing is calculated at the time of initiating the upgrade using current pass prices',
                    'Upgrade payments are non-refundable once processed'
                  ].map((cond, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                      <span>{cond}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Section 7: Google Account & Login */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <LockClosedIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">7. Google Account & Login</h2>
            </div>
            <div className="pl-0 sm:pl-12 text-text-muted leading-relaxed">
              <p>
                NotesCrafter uses Google OAuth for authentication. You are responsible for maintaining the security of your Google account. All activity associated with your NotesCrafter session is your responsibility. We are not liable for any loss or damage resulting from unauthorised access to your account.
              </p>
            </div>
          </motion.div>

          {/* Section 8: Acceptable Use */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <ShieldExclamationIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">8. Acceptable Use</h2>
            </div>
            <div className="pl-0 sm:pl-12 space-y-3">
              <p className="text-text-muted mb-3">You agree not to:</p>
              <ul className="space-y-2 text-text-muted">
                {[
                  'Use NotesCrafter for any unlawful or unauthorised purpose',
                  'Upload content that infringes third-party intellectual property or copyright',
                  'Attempt to reverse-engineer, decompile, or scrape any part of our platform',
                  'Circvent access controls, file size limits, or paywalls through any means',
                  'Upload malicious files, scripts, or content intended to harm our systems or other users',
                  'Share, resell, or transfer your Community Pass or account access to others',
                  'Use automated bots or scripts to interact with our services'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Section 9: Intellectual Property */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <ScaleIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">9. Intellectual Property</h2>
            </div>
            <div className="pl-0 sm:pl-12 space-y-4 text-text-muted leading-relaxed">
              <p>
                All NotesCrafter branding, design, source code, copy, and product features are the intellectual property of NotesCrafter. Unauthorised reproduction or commercial use is prohibited.
              </p>
              <p>
                Files and content you upload remain your property. We claim no ownership over your uploaded documents. Our limited right to store your files applies only to the print service and solely for the purpose of fulfilling your print order.
              </p>
            </div>
          </motion.div>

          {/* Section 10: Third-Party Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.83 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <LockClosedIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">10. Third-Party Services</h2>
            </div>
            <div className="pl-0 sm:pl-12 text-text-muted leading-relaxed">
              <p>
                NotesCrafter integrates with third-party services including Google (authentication), Razorpay (payments), Shiprocket (logistics), and Google AdMob (in-app advertising). Your use of these services is governed by their respective terms and privacy policies. We are not responsible for the actions, availability, or policies of these third parties.
              </p>
            </div>
          </motion.div>

          {/* Section 11: Disclaimer of Warranties */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <ExclamationTriangleIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">11. Disclaimer of Warranties</h2>
            </div>
            <div className="pl-0 sm:pl-12 text-text-muted leading-relaxed">
              <p>
                NotesCrafter services are provided on an "as is" and "as available" basis without warranties of any kind, express or implied. We do not warrant that our services will be uninterrupted, error-free, or free of viruses. Output quality from Flow and Tools depends on the quality and format of your source files.
              </p>
            </div>
          </motion.div>

          {/* Section 12: Limitation of Liability */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.88 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <ExclamationTriangleIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">12. Limitation of Liability</h2>
            </div>
            <div className="pl-0 sm:pl-12 text-text-muted leading-relaxed">
              <p>
                To the fullest extent permitted by applicable Indian law, NotesCrafter shall not be liable for any indirect, incidental, special, consequential, or punitive damages — including but not limited to loss of data, revenue, or business opportunities — arising from your use of or inability to use our services.
              </p>
            </div>
          </motion.div>

          {/* Section 13: Governing Law & Jurisdiction */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <ScaleIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">13. Governing Law & Jurisdiction</h2>
            </div>
            <div className="pl-0 sm:pl-12 text-text-muted leading-relaxed space-y-3">
              <p>
                These Terms of Service are governed by and construed in accordance with the laws of India, including the Information Technology Act, 2000 and the Digital Personal Data Protection Act, 2023. Any disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the competent courts in Aurangabad, Bihar, India.
              </p>
              <p className="bg-primary/5 p-4 border border-primary/10 rounded-lg text-sm">
                For the purposes of legal identification, NotesCrafter is operated by <span className="font-semibold text-text-main text-base">Nitish Kumar</span> (Sole Proprietor).
              </p>
            </div>
          </motion.div>

          {/* Section 14: Modifications to Terms */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.93 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <ExclamationTriangleIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">14. Modifications to Terms</h2>
            </div>
            <div className="pl-0 sm:pl-12 text-text-muted leading-relaxed">
              <p>
                We reserve the right to modify these Terms at any time. Significant changes will be communicated via WhatsApp, email, or a notice on our platform. Continued use of our services following the posting of changes constitutes your acceptance of the revised Terms.
              </p>
            </div>
          </motion.div>

          {/* Section 15: Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95 }}
            className="bg-glass/30 backdrop-blur-md border border-primary/20 rounded-xl p-8 hover:border-primary/40 transition-colors shadow-xl"
          >
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div>
                <h2 className="text-2xl font-bold text-text-main mb-2">15. Contact Us</h2>
                <div className="space-y-1 text-text-muted text-sm sm:text-base">
                  <p className="font-semibold text-text-main">NotesCrafter</p>
                  <p className="flex items-center gap-1.5">
                    <MapPinIcon className="w-4 h-4 text-primary flex-shrink-0" />
                    Sole Proprietorship · Aurangabad, Bihar, India
                  </p>
                </div>
              </div>
              <a
                href="mailto:support@notescrafter.com"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary-hover text-white font-semibold rounded-xl transition-all shadow-lg shadow-primary/25 hover:scale-[1.02] active:scale-[0.98]"
              >
                <EnvelopeIcon className="w-5 h-5" />
                support@notescrafter.com
              </a>
            </div>
          </motion.div>

          {/* Severability Notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
            className="mt-8 bg-primary/5 border border-primary/20 rounded-xl p-6"
          >
            <div className="flex items-start space-x-3">
              <ExclamationTriangleIcon className="w-6 h-6 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-text-main mb-2">Severability</h3>
                <p className="text-text-muted text-sm leading-relaxed">
                  If any provision of these Terms of Service is held to be invalid or unenforceable, 
                  the remaining provisions will remain in full force and effect. The invalid or 
                  unenforceable provision will be replaced with a valid provision that most closely 
                  matches the intent of the original provision.
                </p>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </div>
  );
};

export default TermsOfService;